import { useQuery } from '@tanstack/react-query';
import { CalendarDays, LayoutDashboard } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import type { ActivityKind, NowContext } from '@tempo/core';
import { api } from '../api';
import { MarkPrompt, RedAlertBanner } from '../components/RedAlert';
import { NextCard, TomorrowLine, untilLine } from '../components/assistant/common';
import { Bring, Journey, Kitchen, StudyConcepts, StudyFocus, Until, Workout } from '../components/assistant/DayTools';
import { EmailPopup, EmailTool, emailCount, useEmailPopups } from '../components/assistant/EmailTool';
import { LiveNotes, ModuleInfo, Terms } from '../components/assistant/LectureTools';
import { Loading } from '../components/ui';
import { time } from '../lib/format';
import { useFeatures, useNow } from '../lib/hooks';
import type { Route } from '../lib/router';

const EYEBROW: Record<ActivityKind, string> = {
  lecture: 'Lecture',
  tutorial: 'Tutorial',
  lab: 'Lab',
  study: 'Study',
  travel: 'On the way',
  cooking: 'Cooking',
  gym: 'Workout',
  shift: 'At work',
  sleep: 'Sleep',
  rest: 'Wind down',
  routine: 'Now',
  event: 'Now',
  free: 'Free',
};

interface Tool {
  id: string;
  label: string;
  render: () => ReactNode;
}

/** Pick one to three useful tools for what you're doing. The first is shown; the rest wait as chips. */
function toolsFor(n: NowContext, now: number): Tool[] {
  const tools = activityTools(n, now);
  const email = emailTool(n);
  return email ? [...tools, email] : tools;
}

/**
 * The Email chip is only for free time (and "brief"/admin/inbox routines). During everything else you only see
 * urgent emails as they arrive, as a pop-up (see useEmailPopups).
 */
function emailTool(n: NowContext): Tool | null {
  const { needs, drafts } = emailCount(n);
  const briefSlot = n.activity === 'routine' && /\b(brief\w*|admin|e-?mails?|inbox)\b/i.test(n.item?.title ?? '');
  const show = (n.activity === 'free' || briefSlot) && needs + drafts > 0;
  return show ? { id: 'email', label: needs ? `Email · ${needs}` : 'Drafts', render: () => <EmailTool n={n} /> } : null;
}

function activityTools(n: NowContext, now: number): Tool[] {
  const next: Tool = { id: 'next', label: 'Up next', render: () => <NextCard n={n} now={now} /> };
  switch (n.activity) {
    case 'lecture':
    case 'tutorial':
    case 'lab':
      return [
        { id: 'live', label: 'Live notes', render: () => <LiveNotes n={n} /> },
        { id: 'module', label: n.module?.code ?? 'Module', render: () => <ModuleInfo n={n} /> },
        { id: 'terms', label: 'Terms', render: () => <Terms n={n} /> },
      ];
    case 'study':
      return [
        { id: 'focus', label: 'Focus', render: () => <StudyFocus n={n} now={now} /> },
        ...(n.study?.relatedConcepts.length ? [{ id: 'concepts', label: 'Concepts', render: () => <StudyConcepts n={n} /> }] : []),
        next,
      ];
    case 'travel':
      return [
        { id: 'journey', label: 'Journey', render: () => <Journey n={n} now={now} /> },
        ...(n.bring.length ? [{ id: 'bring', label: 'Bring', render: () => <Bring n={n} /> }] : []),
      ];
    case 'cooking':
      return [{ id: 'kitchen', label: 'Kitchen', render: () => <Kitchen n={n} /> }, next];
    case 'gym':
      return [{ id: 'workout', label: 'Workout', render: () => <Workout n={n} /> }, next];
    case 'shift':
      return [{ id: 'shift', label: 'Shift', render: () => <Until n={n} now={now} /> }];
    case 'sleep':
    case 'rest':
      return [{ id: 'tomorrow', label: 'Tomorrow', render: () => <TomorrowLine n={n} /> }];
    case 'free':
      return [{ id: 'next', label: 'Up next', render: () => <><NextCard n={n} now={now} /><TomorrowLine n={n} /></> }];
    default:
      return [{ id: 'now', label: 'Now', render: () => <Until n={n} now={now} /> }, next];
  }
}

function heroTitle(n: NowContext): string {
  if (n.activity === 'free') return 'Free time';
  if (n.activity === 'sleep') return 'Time to rest';
  if (n.activity === 'travel') return n.nextUp.item ? `To ${n.nextUp.item.title}` : (n.item?.title ?? 'Travelling');
  return n.item?.title ?? 'Now';
}

export function AssistantPage({ route }: { route: Route }) {
  // ?at=<ms or ISO> previews another moment (for testing).
  const at = route.params.get('at');
  const { schoolManager } = useFeatures();
  const q = useQuery({
    queryKey: ['now', at],
    queryFn: () => api.get<NowContext>(`/now${at ? `?at=${encodeURIComponent(at)}` : ''}`),
    refetchInterval: 30_000,
  });
  const wall = useNow(1000);
  // With ?at= the clock runs on from that moment; otherwise it's just now.
  const now = q.data && at ? q.data.now + (wall - q.dataUpdatedAt) : wall;
  const n = q.data;
  const tools = n ? toolsFor(n, now) : [];
  const key = n ? `${n.activity}:${n.item?.id ?? 'none'}` : '';
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => setActive(null), [key]); // a new block → back to its main tool
  const shown = tools.find((t) => t.id === active) ?? tools[0];
  // Urgent email pops up during anything except sleep / winding down.
  const { popups, dismiss } = useEmailPopups(n);
  const popup = n && n.activity !== 'sleep' && n.activity !== 'rest' ? popups[0] : undefined;

  const eyebrow = n ? [EYEBROW[n.activity], n.module?.code].filter(Boolean).join(' · ') : '';
  const sub = !n
    ? null
    : n.activity === 'free'
      ? n.next
        ? `until ${n.next.title} at ${time(n.next.start)}`
        : 'nothing else planned today'
      : n.activity === 'sleep'
        ? n.item
          ? `until ${time(n.item.end)}`
          : null
        : untilLine(n.item, now);

  return (
    <div className={`assistant ${n?.activity === 'sleep' || n?.activity === 'rest' ? 'dim' : ''}`}>
      <div className="assistant-top">
        <span className="row" style={{ gap: 6 }}>
          <a className="pill-link" href="#/today">
            <CalendarDays size={14} /> Calendar
          </a>
          {schoolManager && (
            <a className="pill-link" href="#/everything">
              <LayoutDashboard size={14} /> Everything
            </a>
          )}
        </span>
        {at && <span className="tiny faint">preview · {time(now)}</span>}
      </div>
      <MarkPrompt />
      <RedAlertBanner />
      {q.error ? (
        <div className="assistant-hero">
          <div className="callout danger">
            {(q.error as Error).message === 'Not found' ? 'This Tempo server needs a restart to use the Assistant (close it and run start-tempo.cmd again).' : (q.error as Error).message}
          </div>
        </div>
      ) : q.isLoading || !n ? (
        <Loading />
      ) : (
        <>
          <div className="assistant-hero">
            <div className="eyebrow">{eyebrow}</div>
            <h1 className="title">{heroTitle(n)}</h1>
            {sub && <div className="when">{sub}</div>}
            {n.item?.location && n.activity !== 'travel' && <div className="when faint">{n.item.location}</div>}
          </div>
          {popup && <EmailPopup m={popup} more={popups.length - 1} onDismiss={() => dismiss(popup)} />}
          {shown && (
            <section className="tool" aria-label={shown.label}>
              {shown.render()}
            </section>
          )}
          {tools.length > 1 && (
            <div className="tool-chips">
              {tools
                .filter((t) => t.id !== shown?.id)
                .map((t) => (
                  <button key={t.id} className="chip big" onClick={() => setActive(t.id)}>
                    {t.label}
                  </button>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
